import { useEffect, useRef } from 'react';
import { useMessagingStore } from '@/store/messaging.store';
import { useMessagingSocket } from './useMessagingSocket';

/**
 * Hook that processes scheduled messages and sends them when their time arrives.
 * Checks every 30 seconds for messages that are due to be sent.
 *
 * Messages that are due but cannot be sent (e.g. socket disconnected) are
 * retained and retried on the next interval tick.
 */
export function useScheduledMessages() {
  const { scheduledMessages, removeScheduledMessage } = useMessagingStore();
  const { sendMessage, socket } = useMessagingSocket();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const checkScheduledMessages = () => {
      const current = useMessagingStore.getState().scheduledMessages;
      const now = new Date();

      for (const scheduled of current) {
        const scheduledTime = new Date(scheduled.scheduledAt);

        if (scheduledTime <= now) {
          // Only send if the socket is connected; otherwise leave the
          // message in the queue so it is retried on the next tick.
          if (!socket?.connected) {
            console.warn('[Scheduled Messages] Socket disconnected, deferring:', scheduled.id);
            continue;
          }

          // Send the message
          sendMessage({
            conversation_id: scheduled.conversationId,
            content: scheduled.content,
            type: 'text',
          });

          // Remove from scheduled list
          removeScheduledMessage(scheduled.id);

          console.log('[Scheduled Messages] Sent scheduled message:', scheduled.id);
        }
      }
    };

    // Check immediately on mount
    checkScheduledMessages();

    // Then check every 30 seconds
    intervalRef.current = setInterval(checkScheduledMessages, 30000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [sendMessage, removeScheduledMessage, socket]);

  return {
    pendingCount: scheduledMessages.length,
  };
}
